'use client'

import { KeyboardEvent, useState } from 'react'

import { Cell, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { KeyboardKey, onA11yKeyDown } from '@marketplace-web/vendor-abstractions/web-ui-util'
import { orderDetailsProtectionPoliciesClickEvent } from '@marketplace-web/seller-protection/seller-protection-data'
import { BuyerProtectionModal } from '@marketplace-web/escrow-pricing/escrow-pricing-feature'

type Props = {
  transactionId: number | undefined
  transactionStatus: number | null | undefined
  shipmentStatus: number | null | undefined
  isSellerBusiness: boolean
}

const ConversationDetailsBuyerProtection = ({
  transactionId,
  transactionStatus,
  shipmentStatus,
  isSellerBusiness,
}: Props) => {
  const translate = useTranslate('buyer_protection_policies')
  const { track } = useTracking()

  const [isDialogVisible, setIsDialogVisible] = useState(false)

  const trackBuyerProtectionClick = () => {
    if (!transactionId) return

    track(
      orderDetailsProtectionPoliciesClickEvent({
        screen: 'conversation_details',
        target: 'buyer_protection_learn_more',
        transactionId: transactionId.toString(),
        details: JSON.stringify({
          transaction_status: transactionStatus,
          shipment_status: shipmentStatus,
        }),
      }),
    )
  }

  const openBuyerProtectionDialog = () => {
    trackBuyerProtectionClick()
    setIsDialogVisible(true)
  }

  const onKeyDown = (event: KeyboardEvent) => {
    onA11yKeyDown(
      event,
      { keys: [KeyboardKey.Enter, KeyboardKey.Space] },
      () => openBuyerProtectionDialog,
    )
  }

  return (
    <>
      <Cell
        title={<Text text={translate('title')} type="section-heading" as="span" />}
        body={
          <>
            <Spacer size="large" />
            <div className="member-protection-policy_list">
              <Text text={translate('description')} as="span" html />
            </div>
            <Spacer />
            <button
              type="button"
              tabIndex={0}
              onKeyDown={onKeyDown}
              onClick={openBuyerProtectionDialog}
            >
              <Text text={translate('learn_more')} as="span" clickable />
            </button>
          </>
        }
        divider
      />
      <BuyerProtectionModal
        isVisible={isDialogVisible}
        onClose={() => setIsDialogVisible(false)}
        sellerIsBusinessUser={isSellerBusiness}
      />
    </>
  )
}

export default ConversationDetailsBuyerProtection
